import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mapKeys, reduce } from 'lodash-es';
import type { MenuItem, MenuGroup } from '../../types';

interface MenuState {
  items: Record<number, MenuItem>;
  groups: Record<number, MenuGroup>;
  groupMembers: Record<number, number[]>;       // group_id -> [item_id, ...]
  groupGroupComplements: Record<number, number[]>; // group_id -> [complement_group_id, ...]
  itemGroupComplements: Record<number, number[]>;  // item_id -> [complement_group_id, ...]
}

const initialState: MenuState = {
  items: {},
  groups: {},
  groupMembers: {},
  groupGroupComplements: {},
  itemGroupComplements: {},
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    // Items
    setMenuItems(state, action: PayloadAction<MenuItem[]>) {
      // Drop soft-deleted items (mirrors group handling) so they don't linger in
      // the editor/order lists after a delete.
      const active = action.payload.filter((i) => !i.deleted);
      state.items = mapKeys(active, 'id') as unknown as Record<number, MenuItem>;
    },
    pushMenuItem(state, action: PayloadAction<MenuItem>) {
      const item = action.payload;
      if (item.deleted) {
        delete state.items[item.id];
        return;
      }
      state.items[item.id] = item;
    },
    deleteMenuItem(state, action: PayloadAction<number>) {
      delete state.items[action.payload];
    },

    // Groups
    setMenuGroups(state, action: PayloadAction<MenuGroup[]>) {
      const active = action.payload.filter(g => !g.deleted);
      state.groups = mapKeys(active, 'id') as unknown as Record<number, MenuGroup>;
    },
    pushMenuGroup(state, action: PayloadAction<MenuGroup>) {
      const group = action.payload;
      if (group.deleted) {
        delete state.groups[group.id];
        return;
      }
      state.groups[group.id] = group;
    },
    deleteMenuGroup(state, action: PayloadAction<number>) {
      delete state.groups[action.payload];
    },

    // Group Members (menu_group_id -> [menu_item_id, ...])
    setGroupMembers(state, action: PayloadAction<Array<{ menu_group_id: number; menu_item_id: number }>>) {
      state.groupMembers = reduce(action.payload, (result: Record<number, number[]>, kv) => {
        (result[kv.menu_group_id] || (result[kv.menu_group_id] = [])).push(kv.menu_item_id);
        return result;
      }, {});
    },
    pushGroupMember(state, action: PayloadAction<{ menu_group_id: number; menu_item_id: number }>) {
      const { menu_group_id, menu_item_id } = action.payload;
      if (!state.groupMembers[menu_group_id]) {
        state.groupMembers[menu_group_id] = [];
      }
      if (!state.groupMembers[menu_group_id].includes(menu_item_id)) {
        state.groupMembers[menu_group_id].push(menu_item_id);
      }
    },
    deleteGroupMember(state, action: PayloadAction<{ menu_group_id: number; menu_item_id: number }>) {
      const { menu_group_id, menu_item_id } = action.payload;
      if (state.groupMembers[menu_group_id]) {
        state.groupMembers[menu_group_id] = state.groupMembers[menu_group_id].filter(id => id !== menu_item_id);
      }
    },

    // Group-Group Complements
    setGroupGroupComplements(state, action: PayloadAction<Array<{ menu_group_id: number; complement_menu_group_id: number }>>) {
      state.groupGroupComplements = reduce(action.payload, (result: Record<number, number[]>, kv) => {
        (result[kv.menu_group_id] || (result[kv.menu_group_id] = [])).push(kv.complement_menu_group_id);
        return result;
      }, {});
    },
    pushGroupGroupComplement(state, action: PayloadAction<{ menu_group_id: number; complement_menu_group_id: number }>) {
      const { menu_group_id, complement_menu_group_id } = action.payload;
      if (!state.groupGroupComplements[menu_group_id]) {
        state.groupGroupComplements[menu_group_id] = [];
      }
      if (!state.groupGroupComplements[menu_group_id].includes(complement_menu_group_id)) {
        state.groupGroupComplements[menu_group_id].push(complement_menu_group_id);
      }
    },
    deleteGroupGroupComplement(state, action: PayloadAction<{ menu_group_id: number; complement_menu_group_id: number }>) {
      const { menu_group_id, complement_menu_group_id } = action.payload;
      if (state.groupGroupComplements[menu_group_id]) {
        state.groupGroupComplements[menu_group_id] = state.groupGroupComplements[menu_group_id].filter(id => id !== complement_menu_group_id);
      }
    },

    // Item-Group Complements
    setItemGroupComplements(state, action: PayloadAction<Array<{ menu_item_id: number; complement_menu_group_id: number }>>) {
      state.itemGroupComplements = reduce(action.payload, (result: Record<number, number[]>, kv) => {
        (result[kv.menu_item_id] || (result[kv.menu_item_id] = [])).push(kv.complement_menu_group_id);
        return result;
      }, {});
    },
    pushItemGroupComplement(state, action: PayloadAction<{ menu_item_id: number; complement_menu_group_id: number }>) {
      const { menu_item_id, complement_menu_group_id } = action.payload;
      if (!state.itemGroupComplements[menu_item_id]) {
        state.itemGroupComplements[menu_item_id] = [];
      }
      if (!state.itemGroupComplements[menu_item_id].includes(complement_menu_group_id)) {
        state.itemGroupComplements[menu_item_id].push(complement_menu_group_id);
      }
    },
    deleteItemGroupComplement(state, action: PayloadAction<{ menu_item_id: number; complement_menu_group_id: number }>) {
      const { menu_item_id, complement_menu_group_id } = action.payload;
      if (state.itemGroupComplements[menu_item_id]) {
        state.itemGroupComplements[menu_item_id] = state.itemGroupComplements[menu_item_id].filter(id => id !== complement_menu_group_id);
      }
    },
  },
});

export const {
  setMenuItems, pushMenuItem, deleteMenuItem,
  setMenuGroups, pushMenuGroup, deleteMenuGroup,
  setGroupMembers, pushGroupMember, deleteGroupMember,
  setGroupGroupComplements, pushGroupGroupComplement, deleteGroupGroupComplement,
  setItemGroupComplements, pushItemGroupComplement, deleteItemGroupComplement,
} = menuSlice.actions;

export default menuSlice.reducer;
